import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
} from 'src/app/core/dx-types';

import { DxListsStorage, IDxList } from '../../../core/dx-lists.storage';
import { goToGdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { GdIcons } from '../../../ui/app-gd.icons';
import { HdIcons } from '../../../ui/app-hd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	initRankingList,
	navigateToRanking,
	navigateToRankingCreate,
	openDeleteConfirmationPopup,
	openDuplicateConfirmationPopup,
	RankingSelector,
	refreshRankingList,
	toggleRankingStatus,
	updateIncludePublic,
} from '../../store';
import { RankingItemEntity } from './ranking-item.entity';
import { RankingListEntity } from './ranking-list.entity';

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};
@Component({
	selector: 'app-ranking-list',
	templateUrl: './ranking-list.component.html',
	styleUrls: ['./ranking-list.component.scss'],
	inputs: ['projectLabel'],
})
export class RankingListComponent extends DcBaseComponent implements OnInit {
	protected readonly DcIcons = DcIcons;

	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public paths: Array<IBreadCrumbEntry> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const ranking = item as RankingItemEntity;
				this.toggleActivationStatus(ranking);
			},
			icon: ActionIcons.ActivateElement,
		},
	];
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const ranking = item as RankingItemEntity;
				this.toggleActivationStatus(ranking);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: RankingListEntity;
		includePublic: boolean;
	}>;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	private readonly rankingListSubject = new ReplaySubject<RankingListEntity>(1);

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	protected readonly GdIcons = GdIcons;
	protected readonly HdIcons = HdIcons;
	public constructor(
		private readonly store: Store,
		private readonly rankingSelector: RankingSelector,
		private readonly dxStorage: DxListsStorage,

		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'ranking-list';
		this.dxLocalStorageKey = DcDxGrids.RankingListStandalone;

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.rankingSelector.getLoading$(),
			list: this.rankingSelector.getRankingList$(),
			includePublic: this.rankingSelector.includePublic$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initRankingList());

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToGdHome()),
						},
						{
							label: $localize`:i18n=@@ranking.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.rankingSelector
			.getRankingList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();

					this.rankingListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshRankingList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}
	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public createNewRanking(): void {
		this.store.dispatch(navigateToRankingCreate());
	}

	public navigateToRanking(ranking: RankingItemEntity): void {
		this.store.dispatch(
			navigateToRanking({
				id: ranking.id,
			})
		);
	}

	public duplicateRanking(event: Event, ranking: RankingItemEntity): void {
		event.stopPropagation();
		this.store.dispatch(
			openDuplicateConfirmationPopup({
				rankingId: [ranking.id],
				rankingLabel: ranking.label,
			})
		);
	}

	public deleteRanking(event: Event, ranking: RankingItemEntity): void {
		event.stopPropagation();
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				rankingId: [ranking.id],
				rankingLabel: ranking.label,
			})
		);
	}

	public toggleIncludePublic(value: MatSelectChange): void {
		this.store.dispatch(
			updateIncludePublic({
				includePublic: value.value,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: RankingItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: RankingItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedRankings = await firstValueFrom(
			this.rankingSelector.getSelectedRankings$(selectedRowKeys)
		);
		if (selectedRankings.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					rankingId: [selectedRankings[0].id],
					rankingLabel: selectedRankings[0].label,
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				rankingId: selectedRankings.map((k) => k.id),
				rankingLabel: '',
			})
		);
	}

	private toggleActivationStatus(ranking: RankingItemEntity): void {
		this.store.dispatch(
			toggleRankingStatus({
				rankingId: ranking.id,
				isActive: !ranking.isActive,
			})
		);
	}
	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
			ActionIcons.MoveDatablock,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@ranking.list.columnSelection:`,
				typed: '',
			},
			{
				order: 2,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@ranking.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
